import React, { forwardRef, useState } from 'react';
import classNames from 'classnames';
import moment from 'moment';

import UnitDetails from '../UnitAssignment/UnitDetails';
import SlotArrivalStatus from './SlotArrivalStatus';
import ActionsDropdown from './ActionsDropdown';
import ShiftSlotDisplay from './ShiftSlotDisplay';
import editPencil from '../../../assets/images/edit-pencil.svg';
import Badge from '../../../components/badges/BaseBadge';
import CompassIcon from '../../../components/icons/CompassIcon';
import GuaranteedIcon from '../../../components/icons/GuaranteedIcon';
import BundleBadge from '../BundleBadge';
import ShiftTypeBadge from '../ShiftTypeBadge';
import Utils from '../../../utils/utils';
import { GLOBAL_CALENDAR_SHIFT_TYPES } from '../../../constants';
import OnCallSlotBadge from '../OnCallSlotBadge';
import ArrowSplitIcon from '../../../assets/images/arrow-split-gray.svg';
import ArrowSplitPartialCancelledIcon from '../../../assets/images/arrow-split-partial-cancelled.svg';
import InfoCircleIcon from '../../../assets/images/info-icon-purple.svg';
import SplitShiftDetailsModal from '../SplitShift/SplitShiftDetailsModal';
import { getPartialCancelLabel } from '../SplitShift/segmentUtils'

const formatTime = (time) => moment.utc(time).format('hh:mm A');

const MobileRow = ({ label, children, column, className }) => (
	<div className="row">
		<div className="shift-label col-3">{label}</div>
		<div className={classNames('col-9 ps-3', className, { 'd-flex flex-wrap': !column })}>{children}</div>
	</div>
);

const ShiftSlotMobile = forwardRef(
	(
		{
			slot,
			units,
			user,
			updateSlotArrivalStatus,
			onConfirm,
			onPrepareCancel,
			handleEditBonusModalButton,
			handleAddBonusModalButton,
			preventGuaranteeOrBonusUpdates,
			onGuaranteedCheckboxChange,
			onGuaranteeBundleChange,
			setShowUnitAssignmentModal,
			setUnitAssignmentSlot,
			getUnitName,
			onAssign,
			routeSlotAction,
			shouldShowRouteBadges,
			facilityFloatSlotAction,
			onSplitShift
		},
		ref
	) => {
		const [showSplitShiftDetails, setShowSplitShiftDetails] = useState(false);

		const hasAdjustedStart = !!slot.split_adjusted_start_time;
		const hasAdjustedEnd = !!slot.split_adjusted_end_time;
		const isTrimmed = hasAdjustedStart || hasAdjustedEnd;
		const effectiveStartTime = slot.split_adjusted_start_time || slot.raw_start_time;
		const effectiveEndTime = slot.split_adjusted_end_time || slot.raw_end_time;
		const splitChipIcon = isTrimmed ? ArrowSplitPartialCancelledIcon : ArrowSplitIcon;

		const openSplitShiftDetails = () => setShowSplitShiftDetails(true);
		const closeSplitShiftDetails = () => setShowSplitShiftDetails(false);

		return (
			<div
				ref={ref}
				className={classNames('shift-card row with-border with-padding slot', {
					[`slot__${slot.is_flex ? 'flex' : 'shiftmed'}`]: shouldShowRouteBadges && slot.caregiver_name
				})}
			>
				{shouldShowRouteBadges && (
					<MobileRow label="Route" className="with-gap-6">
						<div className="route-badge">
							<ShiftTypeBadge
								shiftType={slot.is_flex ? GLOBAL_CALENDAR_SHIFT_TYPES.FLEX : GLOBAL_CALENDAR_SHIFT_TYPES.ON_DEMAND}
							/>
						</div>
						{!slot.caregiver_id && !slot.is_flex && !!slot.is_hybrid && (
							<div className="route-badge">
								<ShiftTypeBadge shiftType={GLOBAL_CALENDAR_SHIFT_TYPES.FLEX} />
							</div>
						)}
					</MobileRow>
				)}

				<MobileRow label="Assignment" column={slot.has_segments}>
					<span className="fw-bold">{slot.resource_type} &nbsp;&nbsp;</span>{' '}
					<UnitDetails unitId={slot.unit_id} hasSegments={slot.has_segments} />
					{slot.has_segments && (
						<div>
							<button
								type="button"
								className="split-shift-chip split-shift-chip--button"
								onClick={openSplitShiftDetails}
							>
								<img src={splitChipIcon} className="split-shift-chip__icon" alt="Show Segments" />
								<span>Split shift ({slot.segments_count})</span>
								<img src={InfoCircleIcon} className="split-shift-chip__info" alt="Show Segments" />
							</button>
						</div>
					)}
				</MobileRow>

				<MobileRow label="Date">
					<span className="week-day">{slot.shift_day},&nbsp;</span>
					<span className="month-date">{slot.shift_date}</span>
				</MobileRow>

				<MobileRow label="Time" column={isTrimmed}>
					{isTrimmed && (
						<div>
							<span className="slot__trim-badge slot__trim-badge--mobile">
								{getPartialCancelLabel(slot)}
							</span>
						</div>
					)}
					<div>
						{formatTime(effectiveStartTime)} - {formatTime(effectiveEndTime)}
					</div>
					{isTrimmed && (
						<div className="shift-time-mobile__original">
							{formatTime(slot.raw_start_time)} - {formatTime(slot.raw_end_time)}
						</div>
					)}
				</MobileRow>

				<MobileRow label="Professional" className="caregiver" column>
					<ShiftSlotDisplay slot={slot} isMobile />
				</MobileRow>

				<MobileRow label="Details" className="mobile-badges-row with-gap-4">
					{slot.is_flex ? (
						'N/A'
					) : (
						<>
							{slot.is_guaranteed && (
								<Badge
									badgeCopy="Guaranteed"
									icon={<GuaranteedIcon width={16} height={16} fillColor="#00b4c3" color="#FFFFFF" />}
									className="guaranteed-badge"
								/>
							)}
							{slot.contract_id && (
								<BundleBadge shiftsCount={slot.contract_non_cancelled_slots_count} bundleId={slot.contract_id} />
							)}
							{slot.is_orientation && (
								<Badge badgeCopy="Orientation" className="orientation-badge" icon={<CompassIcon />} />
							)}
							{Utils.shouldShowIntegratedBadge(slot, slot.has_integration_badge) && (
							<Badge badgeCopy="Integrated Shift" className="integrated-badge" />
						)}
							{slot.is_on_call_shift && <OnCallSlotBadge slot={slot} />}
						</>
					)}
				</MobileRow>

				<MobileRow label="Bonus">
					{slot.is_flex ? (
						'N/A'
					) : (
						<>
							<b
								className={classNames('bonus-value', {
									'show-mobile-add': !slot.caregiver_name && !slot.facility_payable_bonus
								})}
							>
								{slot.facility_payable_bonus ? `$${slot.facility_payable_bonus}` : ''}
							</b>
							{slot.facility_payable_bonus ? (
								<img
									className={classNames('edit-pencil-icon', {
										show: !slot.caregiver_name
									})}
									role="presentation"
									src={editPencil}
									alt="Edit"
									onKeyDown={handleEditBonusModalButton}
									onClick={handleEditBonusModalButton}
								/>
							) : (
								<img
									className={classNames('edit-pencil-icon', {
										show: !slot.caregiver_name,
										'add-icon': !slot.facility_payable_bonus && !slot.caregiver_name
									})}
									role="presentation"
									src={editPencil}
									alt="Add"
									onKeyDown={handleAddBonusModalButton}
									onClick={handleAddBonusModalButton}
								/>
							)}
						</>
					)}
				</MobileRow>

				<MobileRow label="Status" column>
					{slot.is_flex ? 'N/A' : <SlotArrivalStatus slot={slot} updateSlotArrivalStatus={updateSlotArrivalStatus} />}
				</MobileRow>

				<MobileRow label="Actions" className="justify-content-center">
					<ActionsDropdown
						isFlex={slot.is_flex}
						canGuarantee={!slot.is_orientation && !preventGuaranteeOrBonusUpdates}
						getUnitName={getUnitName}
						hasAvailableUnits={units.length > 0}
						hasUnitsEnabled={user.hasUnitsEnabled}
						onCancel={onPrepareCancel}
						onAssign={onAssign}
						onConfirm={onConfirm}
						onGuaranteedCheckboxChange={onGuaranteedCheckboxChange}
						onGuaranteeBundleChange={onGuaranteeBundleChange}
						permissions={user.permissions}
						setShowUnitAssignmentModal={setShowUnitAssignmentModal}
						setUnitAssignmentSlot={setUnitAssignmentSlot}
						slot={slot}
						onRouteSlot={routeSlotAction}
						onFacilityFloatSlot={facilityFloatSlotAction}
						onSplitShift={onSplitShift}
					/>
				</MobileRow>

				{showSplitShiftDetails && (
					<SplitShiftDetailsModal
						slotId={slot.slot_id}
						managementCompanyId={slot.management_company_id}
						segmentsCount={slot.segments_count}
						onClose={closeSplitShiftDetails}
					/>
				)}
			</div>
		);
	}
);

export default ShiftSlotMobile;
